import { useMemo } from 'react';

import type {
  UseRecordedWorkflowSkills,
  UseWorkflowSkillRoutines,
  WorkflowSkillWithKind,
} from '../../../contracts/workflow-skill';

/**
 * All enabled workflows (instructed + recorded) for the slash picker. Both reads
 * are the app's ports: the routine list with its Workflows verdict, and the
 * recorder's skills, which stand in for their routine by recording id.
 * `enabled: false` observes both list caches without fetching either one.
 */
export const useAvailableWorkflowSkillsWithWorkflowsCore = (
  useWorkflowSkillRoutines: UseWorkflowSkillRoutines,
  useRecordedWorkflowSkills: UseRecordedWorkflowSkills,
  { enabled = true }: { enabled?: boolean } = {}
): readonly WorkflowSkillWithKind[] => {
  const { isWorkflowsEnabled, data: workflowsData } = useWorkflowSkillRoutines({
    enabled,
  });
  const recordedSkills = useRecordedWorkflowSkills({ enabled });

  return useMemo(() => {
    if (!isWorkflowsEnabled) {
      return recordedSkills.map((skill) => ({ ...skill, kind: 'skill' as const }));
    }

    if (!workflowsData?.workflows) return [];

    const skills: WorkflowSkillWithKind[] = [];
    for (const r of workflowsData.workflows) {
      if (!r.enabled) continue;
      if (r.source === 'recorded' && r.source_recording_id) {
        const recordedSkill = recordedSkills.find(
          (skill) => skill.recordingId === r.source_recording_id
        );
        if (!recordedSkill) continue;
        skills.push({
          ...recordedSkill,
          slug: r.slug,
          title: r.name,
          kind: 'skill',
          description: r.description,
        });
      } else {
        skills.push({
          skillId: r.workflow_id,
          slug: r.slug,
          title: r.name,
          kind: 'workflow',
          description: r.description,
        });
      }
    }
    return skills;
  }, [isWorkflowsEnabled, recordedSkills, workflowsData]);
};
